import React, { useEffect } from "react";
import { useLikeStore } from "../stores/likeStore";
import HeartIcon from "./HeartIcon";

interface ReviewLikeCountProps {
  reviewId: string;
  initialLikeCount: number;
  initialIsLiked?: boolean;
}

/**
 * 리뷰 좋아요 표시/토글.
 * 상태는 전역 likeStore로 단일화한다 — 피드·리뷰상세·사진상세·가게시트가 모두
 * 같은 소스를 보므로, 리마운트/새로고침/화면이동 후에도 표시 상태가 서버와 어긋나지
 * 않는다. (예전엔 컴포넌트 로컬 state라, stale prop으로 재시드 → blind-flip 서버 toggle과
 * 어긋나 "가끔 두 번 눌러야" 하는 버그가 있었음.)
 */
const ReviewLikeCount: React.FC<ReviewLikeCountProps> = ({
  reviewId,
  initialLikeCount,
  initialIsLiked = false,
}) => {
  const likeState = useLikeStore((s) => s.likes.get(reviewId));
  const isProcessing = useLikeStore((s) => s.processingIds.has(reviewId));
  const syncFromServer = useLikeStore((s) => s.syncFromServer);
  const toggleLike = useLikeStore((s) => s.toggleLike);

  // 서버에서 새로 받아온 값이 오면 스토어를 갱신한다.
  // "값이 있으면 두기"로 두면 남이 누른 좋아요가 영영 반영되지 않는다
  // (내 토글이 처리 중일 때만 syncFromServer가 알아서 비켜준다).
  useEffect(() => {
    syncFromServer(reviewId, initialIsLiked, initialLikeCount);
  }, [reviewId, initialIsLiked, initialLikeCount, syncFromServer]);

  const isLiked = likeState?.isLiked ?? initialIsLiked;
  const likeCount = likeState?.likeCount ?? initialLikeCount;

  const handleClick = () => {
    if (isProcessing) return;
    // 스토어에 아직 값이 없을 수 있다(마운트 직후). 그때는 화면이 보여주고 있는
    // 값을 넘겨 낙관적 업데이트가 건너뛰어지지 않게 한다.
    void toggleLike(reviewId, { isLiked, likeCount }).catch(() => {});
  };

  return (
    <div
      className="flex items-center gap-8px cursor-pointer"
      onClick={handleClick}
    >
      {/* 아웃라인 실측 1.6px 목표: viewBox22를 20px로 축소 → strokeWidth 1.76 × 20/22 ≈ 1.6 */}
      <HeartIcon filled={isLiked} size={20} strokeWidth={1.76} />
      <span className="text-style-button2 text-textColorText2 text-left leading-none">
        {likeCount}
      </span>
    </div>
  );
};

export default ReviewLikeCount;
